<template>
  <v-dialog v-model="dialogVisible" max-width="620" persistent>
    <v-card class="lesson-form-card">
      <!-- Header -->
      <v-card-title class="form-header px-6 py-5 border-b mb-2">
        <span class="text-lg font-bold text-gray-800">{{ isEdit ? 'Edit Lesson' : 'Add New Lesson' }}</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon color="grey darken-2">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="lessonForm" v-model="valid" lazy-validation>
          <v-text-field
            v-model="form.title"
            label="Lesson Title"
            :rules="[(v) => !!v || 'Title is required']"
            outlined
            dense
            class="mb-3"
            prepend-inner-icon="mdi-play-circle-outline"
          />

          <v-textarea
            v-model="form.description"
            label="Description (optional)"
            outlined
            dense
            rows="3"
            class="mb-3"
            prepend-inner-icon="mdi-text"
          />

          <v-text-field
            v-model.number="form.duration"
            label="Estimated Duration (minutes)"
            type="number"
            min="1"
            outlined
            dense
            class="mb-3"
            prepend-inner-icon="mdi-clock-outline"
            placeholder="e.g. 15"
          />

          <!-- Content Type Selector -->
          <v-radio-group v-model="form.contentType" row class="mb-3 mt-1" hide-details>
            <template v-slot:label>
              <span class="text-xs font-bold grey--text text--darken-2">Content Type</span>
            </template>
            <v-radio label="Video Lesson" value="VIDEO" color="#21427d" class="mr-4 text-sm" />
            <v-radio label="PDF Document" value="PDF" color="#d32f2f" class="text-sm" />
          </v-radio-group>

          <!-- Video Section -->
          <div v-if="form.contentType === 'VIDEO'" class="video-section rounded-xl pa-4 mb-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-sm font-weight-bold grey--text text--darken-3 d-flex align-center">
                <v-icon small class="mr-2" color="#21427d">mdi-video</v-icon>
                Lesson Video
              </div>
              <v-btn
                x-small
                color="#21427d"
                class="white--text normal-case"
                depressed
                :disabled="!courseId || !moduleId || !lessonId"
                @click="openVideoUpload"
              >
                <v-icon left x-small>mdi-upload</v-icon>
                {{ (uploadedContent && uploadedContent.contentType === 'VIDEO') ? 'Replace Video' : 'Upload Video' }}
              </v-btn>
            </div>

            <!-- No video yet -->
            <div v-if="!uploadedContent || uploadedContent.contentType !== 'VIDEO'" class="text-xs grey--text d-flex align-center gap-1">
              <v-icon x-small color="grey">mdi-information-outline</v-icon>
              Save the lesson first, then upload a video from the course detail view.
            </div>

            <!-- Video uploaded / processing -->
            <div v-else class="d-flex align-center gap-3">
              <v-icon color="orange" size="20">mdi-video-check</v-icon>
              <div>
                <div class="text-xs font-weight-bold grey--text text--darken-3">{{ uploadedContent.contentTitle }}</div>
                <v-chip
                  x-small
                  :color="uploadedContent.hlsStatus === 'READY' ? 'success lighten-4' : 'orange lighten-4'"
                  :text-color="uploadedContent.hlsStatus === 'READY' ? 'success darken-2' : 'orange darken-3'"
                  class="mt-1"
                >
                  <v-icon left x-small>{{ uploadedContent.hlsStatus === 'READY' ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
                  {{ uploadedContent.hlsStatus === 'READY' ? 'HLS Ready' : 'Processing…' }}
                </v-chip>
              </div>
            </div>
          </div>

          <!-- PDF Section -->
          <div v-if="form.contentType === 'PDF'" class="pdf-section rounded-xl pa-4 mb-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-sm font-weight-bold grey--text text--darken-3 d-flex align-center">
                <v-icon small class="mr-2" color="#d32f2f">mdi-file-pdf-box</v-icon>
                Lesson PDF Document
              </div>
              <v-btn
                x-small
                color="#d32f2f"
                class="white--text normal-case"
                depressed
                :disabled="!courseId || !moduleId || !lessonId"
                @click="openPdfUpload"
              >
                <v-icon left x-small>mdi-upload</v-icon>
                {{ (uploadedContent && uploadedContent.contentType === 'PDF') ? 'Replace PDF' : 'Upload PDF' }}
              </v-btn>
            </div>

            <!-- No PDF yet -->
            <div v-if="!uploadedContent || uploadedContent.contentType !== 'PDF'" class="text-xs grey--text d-flex align-center gap-1">
              <v-icon x-small color="grey">mdi-information-outline</v-icon>
              Save the lesson first, then upload a PDF from the course detail view.
            </div>

            <!-- PDF uploaded -->
            <div v-else class="d-flex align-center gap-3">
              <v-icon color="green" size="20">mdi-file-check-outline</v-icon>
              <div>
                <div class="text-xs font-weight-bold grey--text text--darken-3">{{ uploadedContent.contentTitle }}</div>
                <div class="text-xxs grey--text mt-0.5">Linked PDF ready to read</div>
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-6 pb-6">
        <v-spacer />
        <v-btn text class="mr-2 normal-case" @click="close">Cancel</v-btn>
        <v-btn
          color="#21427d"
          class="white--text normal-case px-6"
          depressed
          @click="submit"
        >
          <v-icon left small>{{ isEdit ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
          {{ isEdit ? 'Update' : 'Add Lesson' }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Video Upload Dialog -->
    <VideoUploadDialog
      v-if="showVideoUpload && courseId && moduleId && lessonId"
      v-model="showVideoUpload"
      :courseId="courseId"
      :moduleId="moduleId"
      :lessonId="lessonId"
      @uploaded="onContentUploaded"
    />

    <!-- PDF Upload Dialog -->
    <PdfUploadDialog
      v-if="showPdfUpload && courseId && moduleId && lessonId"
      v-model="showPdfUpload"
      :courseId="courseId"
      :moduleId="moduleId"
      :lessonId="lessonId"
      @uploaded="onContentUploaded"
    />
  </v-dialog>
</template>

<script>
import VideoUploadDialog from './VideoUploadDialog.vue'
import PdfUploadDialog from './PdfUploadDialog.vue'

export default {
  name: 'LessonFormDialog',
  components: { VideoUploadDialog, PdfUploadDialog },
  props: {
    value:    { type: Boolean, default: false },
    editData: { type: Object, default: null },
    // Pass these after lesson is created to enable video/pdf upload
    courseId: { type: String, default: null },
    moduleId: { type: String, default: null },
    lessonId: { type: String, default: null }
  },
  data() {
    return {
      valid: false,
      showVideoUpload: false,
      showPdfUpload: false,
      uploadedContent: null,
      form: {
        title:       '',
        description: '',
        duration:    10,
        contentType: 'VIDEO'
      }
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.value },
      set(val) { this.$emit('input', val) }
    },
    isEdit() { return !!this.editData }
  },
  watch: {
    value(val) {
      if (val && this.editData) {
        this.form = {
          title:       this.editData.title || '',
          description: this.editData.description || '',
          duration:    this.editData.estimatedDurationMinutes || 10,
          contentType: this.editData.contentType || 'VIDEO'
        }
        if (this.editData.contentId) {
          this.uploadedContent = {
            contentId: this.editData.contentId,
            contentTitle: this.editData.title,
            contentType: this.editData.contentType || 'VIDEO',
            hlsStatus: this.editData.hlsStatus || 'READY'
          }
        } else {
          this.uploadedContent = null
        }
      } else if (val) {
        this.resetForm()
      }
    }
  },
  methods: {
    openVideoUpload() {
      this.showVideoUpload = true
    },
    openPdfUpload() {
      this.showPdfUpload = true
    },
    onContentUploaded(data) {
      this.uploadedContent = data
      this.$emit('content-uploaded', data)
    },
    resetForm() {
      this.form           = { title: '', description: '', duration: 10, contentType: 'VIDEO' }
      this.uploadedContent = null
      if (this.$refs.lessonForm) this.$refs.lessonForm.resetValidation()
    },
    submit() {
      if (this.$refs.lessonForm && !this.$refs.lessonForm.validate()) return
      this.$emit('save', { ...this.form })
      this.close()
    },
    close() {
      this.resetForm()
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.lesson-form-card { overflow: hidden; }
.form-header { background: white; }
.video-section {
  background: #f8fafd;
  border: 1px solid #e0e8f5;
}
.pdf-section {
  background: #fdf8f8;
  border: 1px solid #f5e0e0;
}
</style>

